import React from 'react';
import Typography from '@material-ui/core/Typography';

import { safeGraphSpec } from './rendering';
import { StaticMetricGraph } from './StaticMetricGraph';

const MAX_METRIC_GRAPHS = 12;
const MAX_CAPTION_LENGTH = 160;

const boundedCaption = (value, fallback) => (
    typeof value === 'string' && value.trim()
        ? value.trim().slice(0, MAX_CAPTION_LENGTH)
        : fallback
);

const DeferredPlot = (props) => {
    // Plotly touches browser-only URL APIs during module initialisation.
    // Defer that dependency until a validated graph is actually displayed.
    // eslint-disable-next-line global-require
    const plotModule = require('react-plotly.js');
    const Plot = plotModule.default || plotModule;
    return <Plot {...props} />;
};

class PlotErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { failed: false };
    }

    static getDerivedStateFromError() {
        return { failed: true };
    }

    render() {
        if (this.state.failed) {
            return this.props.fallback;
        }
        return this.props.children;
    }
}

export const normaliseMetricGraphs = (graphs) => {
    if (!Array.isArray(graphs)) {
        return [];
    }
    return graphs.slice(0, MAX_METRIC_GRAPHS).map((graph, index) => {
        const candidate = graph && typeof graph === 'object' && graph.spec ? graph.spec : graph;
        const spec = safeGraphSpec(candidate);
        if (!spec) {
            return null;
        }
        return {
            id: typeof graph?.id === 'string' ? graph.id.slice(0, 80) : `metric-graph-${index}`,
            caption: boundedCaption(graph?.caption, `Metric graph ${index + 1}`),
            spec,
        };
    }).filter(Boolean);
};

const MetricsPanel = ({ classes, graphs }) => {
    const normalisedGraphs = normaliseMetricGraphs(graphs);
    if (normalisedGraphs.length === 0) {
        return null;
    }
    return (
        <section className={classes.section} aria-labelledby="workflow-metrics-heading">
            <Typography id="workflow-metrics-heading" variant="subtitle2">Metrics</Typography>
            {normalisedGraphs.map((graph) => (
                <figure className={classes.metricFigure} key={graph.id}>
                    <figcaption>
                        <Typography variant="caption" className={classes.muted}>{graph.caption}</Typography>
                    </figcaption>
                    <PlotErrorBoundary
                        key={`${graph.id}-plot`}
                        fallback={<StaticMetricGraph spec={graph.spec} label={graph.caption} />}
                    >
                        <DeferredPlot
                            data={graph.spec.data}
                            layout={{ ...graph.spec.layout, autosize: true }}
                            config={graph.spec.config}
                            useResizeHandler
                            style={{ width: '100%', height: '320px' }}
                        />
                    </PlotErrorBoundary>
                </figure>
            ))}
        </section>
    );
};

export { MetricsPanel };
